import { useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Divider,
  Chip,
  Grid,
  Button,
  TextField,
  Card,
  CardContent,
} from '@mui/material';
import { Close as CloseIcon, Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon, Edit as EditIcon, Delete as DeleteIcon, PowerSettingsNew as ToggleIcon } from '@mui/icons-material';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: 'calc(100% - 2rem)', sm: 'calc(100% - 2rem)', md: '700px' },
  maxWidth: '95vw',
  maxHeight: '90vh',
  bgcolor: 'background.paper',
  borderRadius: 3,
  boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  p: { xs: 2, sm: 2.5, md: 3 },
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column',
};

export const BranchDetailModal = ({ open, branch, onClose, onEdit, onDelete, onToggleStatus }) => {
  const [showPassword, setShowPassword] = useState(false);

  if (!branch) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      BackdropProps={{
        sx: { backdropFilter: 'blur(5px)', backgroundColor: 'rgba(10, 25, 47, 0.55)' },
      }}
    >
      <Box sx={modalStyle}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, gap: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '0.02em', mb: 0.5 }}>
              {branch.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Branch ID: <strong>{branch.id || 'N/A'}</strong>
            </Typography>
            <Chip
              label={branch.is_active ? 'Active' : 'Inactive'}
              color={branch.is_active ? 'success' : 'default'}
              size="small"
              sx={{ fontWeight: 700, borderRadius: 1.5 }}
            />
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ flexShrink: 0, mt: -1 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Top Info Grid */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={7}>
            <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#d1e9ff', bgcolor: '#f5f9ff' }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Branch Information
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Email</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{branch.email || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Phone</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{branch.phone || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">City / State</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{`${branch.city || 'N/A'} • ${branch.state || 'N/A'}`}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Created</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {branch.created_at ? new Date(branch.created_at).toLocaleDateString() : 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={5}>
            <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e8f5e9', bgcolor: '#f3f9f6' }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Location & Access
                </Typography>
                <Typography variant="body2" sx={{ mb: 1.25 }}>
                  {branch.address || 'No address provided'}
                </Typography>
                <Typography variant="caption" color="text.secondary">Coordinates</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {branch.latitude && branch.longitude
                    ? `${branch.latitude}, ${branch.longitude}`
                    : 'Unavailable'}
                </Typography>
                {branch.password && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1.5, gap: 1 }}>
                    <Typography variant="caption" color="text.secondary">Password</Typography>
                    <IconButton
                      size="small"
                      onClick={() => setShowPassword(!showPassword)}
                      sx={{ p: 0.4, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                    >
                      {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {showPassword ? branch.password : '••••••••'}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {branch.latitude && branch.longitude && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Map Preview
            </Typography>
            <Box
              component="iframe"
              src={`https://www.openstreetmap.org/export/embed.html?bbox=${Number(branch.longitude) - 0.02}%2C${Number(branch.latitude) - 0.01}%2C${Number(branch.longitude) + 0.02}%2C${Number(branch.latitude) + 0.01}&layer=mapnik&marker=${branch.latitude}%2C${branch.longitude}`}
              width="100%"
              height="250"
              sx={{ border: '1px solid #ccdde8', borderRadius: 2 }}
              title="branch-map"
            />
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.25, flexWrap: 'wrap' }}>
          <Button
            startIcon={<ToggleIcon />}
            variant="outlined"
            color={branch.is_active ? 'warning' : 'success'}
            onClick={() => onToggleStatus?.(branch)}
            sx={{ minWidth: 120, textTransform: 'none' }}
          >
            {branch.is_active ? 'Deactivate' : 'Activate'}
          </Button>
          <Button
            startIcon={<EditIcon />}
            variant="contained"
            color="primary"
            onClick={() => onEdit?.(branch)}
            sx={{ minWidth: 100, textTransform: 'none' }}
          >
            Edit Branch
          </Button>
          <Button
            startIcon={<DeleteIcon />}
            variant="outlined"
            color="error"
            onClick={() => onDelete?.(branch)}
            sx={{ minWidth: 100, textTransform: 'none' }}
          >
            Delete Branch
          </Button>
          <Button
            variant="text"
            onClick={onClose}
            sx={{ minWidth: 90, textTransform: 'none' }}
          >
            Close
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
